import { useId, useState } from 'react';
import {
  apiLoadErrorText,
  type ChronumLoad,
  type ChronumSnapshotMeta,
  type ChronumStatus,
} from '@shared/api';
import { Button } from '@shared/ui/Button';
import { SkeletonLine } from '@shared/ui/Skeleton';
import { IconChevronDown, IconRefresh } from '@shared/ui/icons';
import {
  CHRONUM_FEEDS,
  countStatuses,
  countsText,
  failureReason,
  formatDateTime,
  isPeriod,
  loadByLabel,
  loadProgressText,
  loadStopped,
  periodLabelLower,
  refreshSummary,
  typo,
  type ChronumFeed,
} from '@entities/chronum';
import { Notice, TextButton } from '@widgets/FleetRegistry/ui';
import { PeriodPicker } from '../PeriodPicker';
import type { ChronumRefresh } from '../useChronumRefresh';
import { Dot, noticeTone } from '../ui';
import {
  Box,
  Connection,
  Controls,
  Feeds,
  FeedsTitle,
  FeedsToggle,
  Hints,
  HintsToggle,
  Live,
  Stopped,
  Top,
} from './styles';
import { FeedCell, FeedsSkeleton, FeedHint } from './FeedCell';
import { LoadProgress } from './LoadProgress';

export interface Props {
  /** Статус — подключение и категории (последний ответ, любого месяца). */
  status: ChronumStatus | undefined;
  /** Статус выбранного месяца: снимки и загрузка месяца; `undefined` — ещё узнаём. */
  monthStatus: ChronumStatus | undefined;
  statusLoading: boolean;
  statusError: unknown;
  onRetryStatus: () => void;
  period: string;
  currentPeriod: string;
  onPeriod: (period: string) => void;
  canWrite: boolean;
  refresh: ChronumRefresh;
}

/** Последний снимок данных для табло; `undefined` — ещё не знаем. */
export function feedSnapshot(
  feed: ChronumFeed,
  status: ChronumStatus | undefined,
): ChronumSnapshotMeta | null | undefined {
  return status ? (status.last[feed.key] ?? null) : undefined;
}

/** Прошлая загрузка месяца не закончилась — и это не та, за которой сейчас следим. */
export function stoppedLoad(
  status: ChronumStatus | undefined,
  refresh: ChronumRefresh,
): ChronumLoad | null {
  const load = status?.period_load;
  if (!load || !loadStopped(load) || refresh.running) return null;
  if (refresh.outcome?.kind === 'finished' && refresh.outcome.load.id === load.id) return null;
  return load;
}

/**
 * Шапка экрана: подключён ли Хронум, что и когда из него пришло по каждой
 * ручке за выбранный месяц, выбор месяца и «Обновить» с ходом загрузки.
 */
export function StatusHeader({
  status,
  monthStatus,
  statusLoading,
  statusError,
  onRetryStatus,
  period,
  currentPeriod,
  onPeriod,
  canWrite,
  refresh,
}: Props) {
  const pickerId = useId();
  const connectionId = useId();
  const noteId = useId();
  const hintsId = useId();
  const feedsId = useId();
  const [hintsOpen, setHintsOpen] = useState(false);
  const [feedsOpen, setFeedsOpen] = useState(false);
  const configured = status?.configured ?? false;
  const outcome = refresh.outcome;
  const summary =
    outcome?.kind === 'finished' ? refreshSummary(outcome.load, outcome.results) : null;
  const feeds = CHRONUM_FEEDS.map((feed) => ({ feed, snapshot: feedSnapshot(feed, monthStatus) }));
  const loaded = feeds.filter((f) => f.snapshot).length;
  const feedsCounts = countsText(
    countStatuses(feeds.flatMap((f) => (f.snapshot ? [f.snapshot.status] : []))),
  );
  const monthBusy =
    (refresh.running && refresh.runningPeriod === period) ||
    monthStatus?.period_load?.status === 'running';
  const stopped = stoppedLoad(monthStatus, refresh);
  const failedPeriod =
    outcome?.kind === 'failed' && isPeriod(outcome.period) ? outcome.period : null;

  return (
    <Box aria-labelledby={connectionId}>
      <Top>
        <Connection>
          {statusLoading ? (
            <>
              <h2 id={connectionId}>Связь с Хронумом</h2>
              <SkeletonLine $height={14} $width="70%" />
            </>
          ) : statusError ? (
            <>
              <h2 id={connectionId}>Связь с Хронумом</h2>
              <Notice $tone="danger" role="alert">
                <span className="text">
                  {apiLoadErrorText(
                    statusError,
                    'Не удалось узнать состояние Хронума.',
                    'Нет доступа к данным Хронума Экономиста.',
                  )}
                </span>
                <TextButton type="button" onClick={onRetryStatus}>
                  Повторить
                </TextButton>
              </Notice>
            </>
          ) : configured ? (
            <>
              <h2 id={connectionId}>
                <Dot $tone="success" aria-hidden="true" />
                Хронум подключён
              </h2>
              <p>
                {typo(
                  'Данные загружаются по кнопке «Обновить» и сами, по расписанию: текущий и прошлый месяц. Каждая загрузка сохраняется снимком — БДР считается по снимкам.',
                )}
              </p>
            </>
          ) : (
            <>
              <h2 id={connectionId}>
                <Dot $tone="warning" aria-hidden="true" />
                Хронум не подключён
              </h2>
              <p id={noteId}>
                {typo(
                  'Администратор должен добавить на сервер ключ Экономиста — до этого данные из Хронума не загружаются. Должности и площадки можно сопоставить заранее.',
                )}{' '}
                Для администратора: переменная окружения <code>CHRONUM_ECON_API_KEY</code>.
              </p>
            </>
          )}
        </Connection>
        <Controls>
          <PeriodPicker id={pickerId} period={period} latest={currentPeriod} onChange={onPeriod} />
          {canWrite && (
            <Button
              type="button"
              $variant="primary"
              disabled={!configured || refresh.starting || monthBusy}
              aria-describedby={
                !configured && !statusLoading && !statusError
                  ? `${connectionId} ${noteId}`
                  : undefined
              }
              aria-label={
                monthBusy || refresh.starting
                  ? undefined
                  : `Обновить данные Хронума за ${periodLabelLower(period)}`
              }
              onClick={() => refresh.run(period)}
            >
              <IconRefresh size={16} />
              {monthBusy || refresh.starting ? 'Загружаю…' : 'Обновить'}
            </Button>
          )}
        </Controls>
      </Top>

      <Live role="status" aria-live="polite">
        {refresh.running && <LoadProgress refresh={refresh} period={period} />}
        {summary && (
          <Notice $tone={noticeTone(summary.tone)}>
            <span className="text">
              <strong>{summary.title}</strong>
              {summary.lines.length > 0 && (
                <ul>
                  {summary.lines.map((l) => (
                    <li key={l}>{l}</li>
                  ))}
                </ul>
              )}
            </span>
            <TextButton type="button" onClick={refresh.dismiss}>
              Скрыть
            </TextButton>
          </Notice>
        )}
        {outcome?.kind === 'failed' && (
          <Notice $tone="danger">
            <span className="text">
              <strong>
                {failedPeriod
                  ? `Загрузку за ${periodLabelLower(failedPeriod)} начать не удалось.`
                  : 'Загрузка не закончилась.'}
              </strong>{' '}
              {outcome.message}
            </span>
            <TextButton type="button" onClick={refresh.dismiss}>
              Скрыть
            </TextButton>
          </Notice>
        )}
      </Live>

      {stopped && !summary && (
        <Stopped>
          <Notice $tone={stopped.status === 'failed' ? 'danger' : 'warning'}>
            <span className="text">
              <strong>
                {typo(
                  `Последняя загрузка за ${periodLabelLower(stopped.period)} не закончилась: готово ${loadProgressText(stopped)}.`,
                )}
              </strong>{' '}
              {typo(
                `${loadByLabel(stopped)}, ${formatDateTime(stopped.started_at)}. ${failureReason(stopped.message) ?? 'Повторите «Обновить».'}`,
              )}
            </span>
          </Notice>
        </Stopped>
      )}

      <FeedsTitle as="h3" id={`${hintsId}-title`}>
        {typo(`Что пришло из Хронума за ${periodLabelLower(period)} — последняя загрузка`)}
      </FeedsTitle>
      <FeedsToggle
        type="button"
        aria-expanded={feedsOpen}
        aria-controls={feedsId}
        onClick={() => setFeedsOpen((v) => !v)}
      >
        <span className="body">
          <span className="title">
            {typo(`Что пришло из Хронума за ${periodLabelLower(period)}`)}
          </span>
          <span className="counts">
            {statusLoading ? 'Узнаю…' : loaded === 0 ? 'Ещё ничего не загружалось' : feedsCounts}
          </span>
        </span>
        <IconChevronDown size={16} />
      </FeedsToggle>
      {statusLoading ? (
        <FeedsSkeleton />
      ) : (
        <Feeds
          id={feedsId}
          data-open={feedsOpen ? 'true' : 'false'}
          aria-labelledby={`${hintsId}-title`}
        >
          {feeds.map(({ feed, snapshot }) => (
            <FeedCell key={feed.key} feed={feed} snapshot={snapshot} />
          ))}
        </Feeds>
      )}
      <HintsToggle
        type="button"
        aria-expanded={hintsOpen}
        aria-controls={hintsId}
        onClick={() => setHintsOpen((v) => !v)}
      >
        <IconChevronDown size={14} />
        Что это за данные и зачем они Экономисту
      </HintsToggle>
      {hintsOpen && (
        <Hints id={hintsId}>
          {CHRONUM_FEEDS.map((f) => (
            <FeedHint key={f.key} feed={f} />
          ))}
        </Hints>
      )}
    </Box>
  );
}
